import React, { useState } from 'react';
import { resolveVideoEmbed, parseYoutubeUrl } from '../lib/videoEmbed';
import { useRegion } from '../contexts/RegionContext';
import { FaYoutube, FaExternalLinkAlt, FaPlay, FaExclamationTriangle } from 'react-icons/fa';

interface VideoPlayerProps {
  src: string;
  title?: string;
  caption?: string;
  className?: string;
  autoPlay?: boolean;
}

export const VideoPlayer: React.FC<VideoPlayerProps> = ({
  src,
  title,
  caption,
  className = '',
}) => {
  const { isIran, language } = useRegion();
  const [hasError, setHasError] = useState(false);
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);

  const embedResult = resolveVideoEmbed(src, isIran);
  const ytDetails = parseYoutubeUrl(src);

  const displayTitle = title || caption || (language === 'fa' ? 'ویدیو بازی' : 'Game Video');

  // If format is completely unavailable or unresolvable
  if (embedResult.kind === 'unavailable' || hasError) {
    const directLink = ytDetails?.watchUrl || (src.startsWith('http') ? src : undefined);

    return (
      <div
        className={`w-full aspect-video rounded-2xl bg-zinc-950 border border-white/10 flex flex-col items-center justify-center p-6 text-center text-gray-300 shadow-2xl relative overflow-hidden group ${className}`}
      >
        {ytDetails?.thumbnailUrl && (
          <img
            src={ytDetails.thumbnailUrl}
            alt={displayTitle}
            className="absolute inset-0 w-full h-full object-cover opacity-20 filter blur-sm group-hover:scale-105 transition-transform duration-500"
          />
        )}
        <div className="relative z-10 max-w-md flex flex-col items-center">
          <div className="w-14 h-14 rounded-full bg-red-600/20 text-red-400 flex items-center justify-center mb-4 border border-red-500/30">
            {ytDetails ? <FaYoutube className="w-8 h-8" /> : <FaExclamationTriangle className="w-6 h-6" />}
          </div>
          <h4 className="text-white font-bold text-base md:text-lg mb-2">{displayTitle}</h4>
          <p className="text-xs md:text-sm text-gray-400 mb-5">
            {language === 'fa'
              ? 'امکان پخش مستقیم این ویدیو در این کلاینت وجود ندارد. می‌توانید آن را مستقیماً مشاهده نمایید:'
              : 'Direct playback is unavailable here. You can open and watch it directly:'}
          </p>
          {directLink ? (
            <a
              href={directLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2.5 bg-red-600 hover:bg-red-700 text-white font-bold text-xs md:text-sm px-5 py-2.5 rounded-xl shadow-lg hover:shadow-red-600/30 transition-all active:scale-95"
            >
              <FaPlay className="w-3.5 h-3.5" />
              <span>{language === 'fa' ? 'مشاهده مستقیم در یوتیوب' : 'Watch on YouTube'}</span>
              <FaExternalLinkAlt className="w-3 h-3 opacity-80" />
            </a>
          ) : (
            <span className="text-xs text-zinc-500">
              {language === 'fa' ? 'آدرس ویدیو نامعتبر است.' : 'Invalid video URL.'}
            </span>
          )}
        </div>
      </div>
    );
  }

  // Direct video file (mp4, webm)
  if (embedResult.kind === 'direct') {
    return (
      <div className={`w-full aspect-video rounded-2xl overflow-hidden shadow-2xl bg-black border border-white/10 ${className}`}>
        <video
          controls
          playsInline
          className="w-full h-full object-cover"
          preload="metadata"
          title={displayTitle}
        >
          <source src={embedResult.src} />
          {language === 'fa' ? 'مرورگر شما از پخش این ویدیو پشتیبانی نمی‌کند.' : 'Your browser does not support the video tag.'}
        </video>
        {caption && (
          <p className="text-center text-xs text-gray-400 mt-2">{caption}</p>
        )}
      </div>
    );
  }

  // YouTube / Aparat / Vimeo / Iframe
  return (
    <div className={`w-full flex flex-col group ${className}`}>
      <div className="relative w-full aspect-video rounded-2xl overflow-hidden shadow-2xl bg-zinc-950 border border-white/10">
        {/* Placeholder skeleton while loading */}
        {!isIframeLoaded && (
          <div className="absolute inset-0 bg-zinc-900 animate-pulse flex items-center justify-center">
            <div className="w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center text-zinc-600">
              <FaPlay className="w-5 h-5 ml-1" />
            </div>
          </div>
        )}

        <iframe
          src={embedResult.src}
          title={displayTitle}
          className={`w-full h-full border-0 transition-opacity duration-300 ${
            isIframeLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          loading="lazy"
          onLoad={() => setIsIframeLoaded(true)}
          onError={() => setHasError(true)}
        />
      </div>

      {/* Caption & optional external link pill */}
      <div className="mt-2.5 px-2 flex items-center justify-between text-xs text-gray-400">
        <span className="font-medium truncate">{caption || displayTitle}</span>
        {embedResult.kind === 'youtube' && (
          <a
            href={embedResult.watchUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-red-400 transition-colors shrink-0 mr-2"
            title={language === 'fa' ? 'مشاهده در یوتیوب' : 'Open in YouTube'}
          >
            <FaYoutube className="w-4 h-4 text-red-500" />
            <span className="text-[11px] underline">
              {language === 'fa' ? 'یوتیوب' : 'YouTube'}
            </span>
            <FaExternalLinkAlt className="w-2.5 h-2.5" />
          </a>
        )}
      </div>
    </div>
  );
};

export default VideoPlayer;
